import type { ArtKind } from '@pujolisique/shared';

/**
 * Client HTTP de l'API PUJOLiSIQUE.
 *  - Web : cookie de session (même origine), base vide.
 *  - App Android (Capacitor) : VITE_API_BASE=https://music.pujolinet.fr et
 *    un fournisseur de jeton Keycloak via setAccessTokenProvider().
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ?? '';

type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;
let streamToken: { token: string; expiresAt: number } | null = null;

export function setAccessTokenProvider(p: TokenProvider | null) {
  tokenProvider = p;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
export function setUnauthorizedHandler(h: UnauthorizedHandler) {
  onUnauthorized = h;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.json !== undefined) headers.set('Content-Type', 'application/json');
  if (tokenProvider) {
    const t = await tokenProvider();
    if (t) headers.set('Authorization', `Bearer ${t}`);
  }
  const res = await fetch(API_BASE + path, {
    ...init,
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
    credentials: API_BASE ? 'omit' : 'same-origin',
  });
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, 'Session expirée');
  }
  if (!res.ok) {
    let msg = `Erreur ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {
      /* corps non JSON */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** En mode Bearer (mobile), les médias ont besoin d'un jeton de flux signé en paramètre. */
export async function ensureStreamToken() {
  if (!tokenProvider) return;
  if (streamToken && streamToken.expiresAt - Date.now() > 10 * 60_000) return;
  streamToken = await api<{ token: string; expiresAt: number }>('/api/stream-token', { method: 'POST' });
}

function withToken(url: string) {
  if (!tokenProvider || !streamToken) return url;
  return url + (url.includes('?') ? '&' : '?') + 'st=' + encodeURIComponent(streamToken.token);
}

/** « album/123 » ou (kind, id) → URL d'image */
/**
 * Pochettes changées dans l'onglet Édition : on contourne le cache du navigateur
 * (une heure). Gardé deux heures dans ce navigateur, même après rechargement.
 */
const ART_KEY = 'pjs-art-versions';
const artVersions = new Map<string, number>();
try {
  const saved = JSON.parse(localStorage.getItem(ART_KEY) ?? '{}') as Record<string, number>;
  for (const [k, v] of Object.entries(saved)) if (Date.now() - v < 2 * 3600_000) artVersions.set(k, v);
} catch {
  /* stockage indisponible */
}
export function bustArt(ref: string) {
  artVersions.set(ref, Date.now());
  try {
    localStorage.setItem(ART_KEY, JSON.stringify(Object.fromEntries(artVersions)));
  } catch {
    /* navigation privée : valable pour cette page seulement */
  }
}

export function artUrl(ref: string | undefined, size?: number): string | undefined;
export function artUrl(kind: ArtKind, id: string, size?: number): string;
export function artUrl(a: string | undefined, b?: string | number, c?: number): string | undefined {
  if (!a) return undefined;
  let path: string;
  let size: number;
  if (typeof b === 'string') {
    path = `${a}/${encodeURIComponent(b)}`;
    size = c ?? 300;
  } else {
    const [kind, ...rest] = a.split('/');
    path = `${kind}/${encodeURIComponent(rest.join('/'))}`;
    size = (b as number | undefined) ?? 300;
  }
  const ver = artVersions.get(typeof b === 'string' ? `${a}/${b}` : a);
  return withToken(`${API_BASE}/api/art/${path}?size=${size}${ver ? `&v=${ver}` : ''}`);
}

export function streamUrl(id: string, bitrate = 0, start = 0): string {
  const q = new URLSearchParams();
  if (bitrate) q.set('bitrate', String(bitrate));
  if (start > 0) q.set('start', start.toFixed(1));
  const qs = q.toString();
  return withToken(`${API_BASE}/api/stream/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`);
}

export function downloadUrl(id: string): string {
  return withToken(`${API_BASE}/api/download/${encodeURIComponent(id)}`);
}

export function loginUrl(returnTo = location.pathname + location.search, popup = false) {
  const q = new URLSearchParams({ returnTo });
  if (popup) q.set('popup', '1');
  return `${API_BASE}/auth/login?${q}`;
}
